// @@/shared/reservations/reservation-events/ReservationCheckOutEarlyEvent.ts

import { timeRangeZodSchema } from "@@/shared/date-time/time-range";
import type {
    _reservationEventBase,
    _reservationEventBaseFactoryParams,
} from "@@/shared/reservations/events/_reservation-base.event";
import {
    _reservationEventBaseFactory,
    _reservationEventBaseZodObject,
} from "@@/shared/reservations/events/_reservation-base.event";
import { statusEventIdZodSchema } from "@@/shared/statuses/status-event-id";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";

// Define the specific data for the CheckOut event
const reservationCheckOutEarlyEventData = z.object({
    type: z.literal("CHECKED_OUT_EARLY"),
    userId: userIdZodSchema.optional(),

    checkoutStatusEventId: statusEventIdZodSchema,

    earlyCheckout: z
        .object({
            originalTimeRange: timeRangeZodSchema,
        })
        .optional(),
});

// Merge base schema with specific data schema
const reservationCheckOutEarlyEventZodSchema =
    _reservationEventBaseZodObject.merge(reservationCheckOutEarlyEventData);

// Infer the TypeScript type
type ReservationCheckOutEarlyEvent = z.infer<
    typeof reservationCheckOutEarlyEventZodSchema
>;

type FactoryParams = Except<
    _reservationEventBaseFactoryParams &
        Except<
            ReservationCheckOutEarlyEvent,
            keyof _reservationEventBase | "type" | "signature"
        >,
    "signature"
> & {
    _version?: number;
};

// Factory
const reservationCheckOutEarlyEventFactory = (
    params: FactoryParams,
): ReservationCheckOutEarlyEvent => {
    const baseEvent = _reservationEventBaseFactory({
        reservationId: params.reservationId,
        providerId: params.providerId,
        _version: params._version,
        signature: [
            params.reservationId,
            "CHECKED_OUT_EARLY",
            [params.checkoutStatusEventId],
        ], // TODO ADD BETTER SIGNATURE
    });

    return {
        ...baseEvent,
        type: "CHECKED_OUT_EARLY",
        userId: params.userId,
        earlyCheckout: params.earlyCheckout,
        checkoutStatusEventId: params.checkoutStatusEventId,
    };
};

// Type guard function
const isReservationCheckOutEarlyEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is ReservationCheckOutEarlyEvent => {
    return (
        !!event &&
        event._type === "RESERVATION_EVENT" &&
        event.type === "CHECKED_OUT_EARLY"
    );
};

export {
    isReservationCheckOutEarlyEvent,
    reservationCheckOutEarlyEventFactory,
    reservationCheckOutEarlyEventZodSchema,
};
export type { ReservationCheckOutEarlyEvent };
